import { saveNeonblueTemplateMutation } from "@/api/generated/@tanstack/react-query.gen";
import { SaveBar } from "@/components/composite/save-bar";
import { Page } from "@/components/misc";
import { Breadcrumb, Button } from "@/components/ui";
import { uploadImage } from "@/lib/upload-image";
import { formatApiError } from "@/lib/utils/api-error";
import { cn } from "@/lib/utils/cn";
import {
  createRegistry,
  DEFAULT_BLOCKS,
  type Doc,
  EmailEditor,
  serializeDoc,
} from "@org/email-editor";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PencilIcon } from "lucide-react";
import React from "react";
import { toast } from "sonner";
import { RenameTemplateDialog } from "./-templates/rename-template-dialog";

const NewTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const registry = React.useMemo(() => createRegistry(DEFAULT_BLOCKS), []);
  const docRef = React.useRef<Doc | null>(null);
  const [name, setName] = React.useState("");
  const [isDirty, setIsDirty] = React.useState(false);
  const [renameOpen, setRenameOpen] = React.useState(false);

  const createMutation = useMutation({
    ...saveNeonblueTemplateMutation(),
    onSuccess: (data) => {
      toast.success("Template created");
      if (data.template_id) {
        void navigate({
          to: "/content/templates/$templateId",
          params: { templateId: data.template_id },
        });
      } else {
        void navigate({ to: "/content/templates" });
      }
    },
    onError: (error) => toast.error(formatApiError(error, "Failed to create template")),
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!docRef.current) {
      toast.error("Editor not ready");
      return;
    }
    createMutation.mutate({
      body: {
        name: name.trim(),
        design_state: serializeDoc(docRef.current, registry),
      },
    });
  };

  return (
    <Breadcrumb label="New template">
      <Page size="full" className="space-y-6 h-full flex flex-col min-h-0 pb-20">
        <Page.TitleContainer>
          <div className="flex min-w-0 flex-col gap-1.5">
            <div className="flex items-center gap-1">
              <Page.Title className={cn(!name && "text-muted-foreground")}>
                {name || "Untitled template"}
              </Page.Title>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setRenameOpen(true)}
                aria-label="Name template"
                className="text-muted-foreground"
              >
                <PencilIcon className="size-4" />
              </Button>
            </div>
            <Page.Subtitle>
              Compose an email with the visual editor. Use {"{{ nb.<variable> }}"} for merge fields.
            </Page.Subtitle>
          </div>
        </Page.TitleContainer>
        <div className="flex-1 min-h-[640px]">
          <EmailEditor
            uploadImage={uploadImage}
            onChange={(doc) => {
              docRef.current = doc;
              setIsDirty(true);
            }}
          />
        </div>
        <SaveBar
          show={isDirty}
          onSave={handleSave}
          isSaving={createMutation.isPending}
          label="Save Changes"
        />

        <RenameTemplateDialog
          open={renameOpen}
          onOpenChange={setRenameOpen}
          initialName={name}
          onRename={(next) => {
            setName(next);
            setIsDirty(true);
          }}
          title="Name template"
          confirmLabel="Save"
        />
      </Page>
    </Breadcrumb>
  );
};

export const Route = createFileRoute("/content/templates/new")({
  component: NewTemplatePage,
});
